// import { fooServiceModule } from '../../services/foo/foo-service';
import fileTreeTemplate from './file-tree.tpl.html';
import './file-tree.less';
import { compareIgnoreCase } from '../../utils/string-utils';
import { accountServiceModule } from '../../services/account-service/account-service';
import { keycodesModule } from '../../constants/keycodes';
import { bubbleDialogComponentModule } from '../bubble-dialog/bubble-dialog';
import { cleanPath, splitPath } from '../../utils/file-utils/file-utils';
import { fileIconComponentModule } from '../file-icon/file-icon';
import { ngContextmenuDirectiveModule } from '../ng-contextmenu/ng-contextmenu';
import { stopEvent } from '../../utils/event-utils';
import { defineValue } from '../../utils/object-utils/object-utils';
import * as ArrayUtils from '../../utils/array-utils';

const MAX_DEPTH = 8;
const NESTED_OFFSET = 14;

class FileRow {
  constructor({
    tree,
    file,
    path,
    filename,
    directory = false,
    parent = null,
    collapsed = directory,
  }) {
    defineValue(this, 'tree', tree);
    defineValue(this, 'parent', parent);
    this.file = file;
    this.path = path || (file && file.label);
    this.filename = filename || (file && file.label);
    // we use `\n` for sorting because it comes before all other allowed characters
    this.dirInlineSortPath =
      ((parent && parent.dirInlineSortPath) || '') + '\n' + filename;
    this.dirTopSortPath =
      ((parent && parent.dirTopSortPath) || '') +
      (directory ? '\n0' : '\n1') +
      filename;
    this.directory = directory;

    this.key =
      ((this.file && this.file.id) || '') +
      ':' +
      (this.file && this.file.reference ? '[ref]/' : '') +
      this.path;

    if (directory) {
      this.collapsed = collapsed;
      this.children = [];
    }

    if (parent) {
      parent.children.push(this);
    }
  }

  _isHidden() {
    return (
      this.parent &&
      ((this.parent.collapsed && !this.parent.compacted) ||
        this.parent._isHidden())
    );
  }

  get hidden() {
    return this.tree.rootCollapsed || this.compacted || !!this._isHidden();
  }

  get label() {
    if (this.file && this.file.label) {
      return this.file.label; // useful for non-file components
    }
    return (
      (this.parent && this.parent.compacted ? this.parent.label + '/' : '') +
      this.filename
    );
  }

  setDepth() {
    this.depth =
      (this.compacted ? 0 : 1) + (this.parent ? this.parent.depth : 0);
    this.leftPadding = `${(Math.min(this.depth, MAX_DEPTH) - 1) * NESTED_OFFSET}px`;
  }

  setCollapsed(val) {
    if (this.directory) {
      this.collapsed = val;
      if (this.file) {
        this.file.collapsed = val;
      }
    }
  }
}

export const fileTreeComponentModule = angular
  .module('Q.components.file-tree', [
    accountServiceModule,
    keycodesModule,
    bubbleDialogComponentModule,
    fileIconComponentModule,
    ngContextmenuDirectiveModule,
  ])

  .component('fileTree', {
    bindings: {
      // if true, the root (header) is collapsed (and collapseable), which is useful for things like
      // deleted files
      collapseRoot: '<',

      // header settings
      headerLabel: '@',
      headerTooltip: '@',
      headerActions: '<',
      hideFileTreeMenu: '<',
      onFileAddClick: '&',

      // tree config
      files: '<',
      selectedFile: '<',
      onFileClick: '&',
      fileMenuClass: '&',
      fileMenuTemplate: '<',
      disableSort: '<',
      autoExpandAll: '<',
      reviewMode: '<',
      expired: '<',
      disabled: '<',
      readOnly: '<',
      // if true, ignore file paths
      flat: '<',
      // if true, shows an X that removes files from the files array
      filesCloseable: '<',

      // highlights files that are open
      openFiles: '<',
      // highlights files that were changed
      changedFiles: '<',
    },
    require: {
      multiFileChallengesCtrl: '^?linkMultiFileChallenges',
    },
    controller: 'FileTreeCtrl',
    template: fileTreeTemplate,
  })

  .controller(
    'FileTreeCtrl',
    function FileTreeCtrl(
      $scope,
      $attrs,
      accountService,
      KeyCodes,
      bubbleDialogService,
      $rootScope,
      $log,
    ) {
      const ctrl = this;
      ctrl.fileRows = [];

      ctrl.$onInit = () => {
        loadPrefs();
        ctrl.rootCollapsed = ctrl.collapseRoot;
        ctrl.treeCloseable = !!$attrs.onCloseTree;
        updateFilePaths();
        if ($attrs.onFileAddClick) {
          ctrl.showAddButton = true;
        }

        // watch the file paths, since we only really care about file paths
        $scope.$watch(
          () =>
            (ctrl.files || [])
              .map((f) => `${f.id}!${f.hidden}!${f.path}`)
              .join('\n'),
          updateFilePaths,
        );

        if (ctrl.multiFileChallengesCtrl) {
          ctrl.multiFileChallengesCtrl.linkFileTree(ctrl);
          $scope.$on('$destroy', () => {
            ctrl.multiFileChallengesCtrl.unlinkFileTree(ctrl);
          });
        }
      };

      ctrl.refresh = updateFilePaths;

      ctrl.handleClick = ($event, fileRow) => {
        if (fileRow.directory) {
          fileRow.setCollapsed(!fileRow.collapsed);
        }
        if (fileRow.file) {
          ctrl.onFileClick({
            $event,
            file: fileRow.file,
            index: fileRow.index,
          });
        }
      };

      ctrl.collapseAll = () => {
        if (ctrl.collapseRoot) {
          ctrl.rootCollapsed = true;
        }
        ctrl.fileRows.forEach((fr) => {
          fr.setCollapsed(true);
        });
      };
      ctrl.expandAll = () => {
        ctrl.rootCollapsed = false;
        if (ctrl.showChangedOnly) {
          ctrl.toggleChanged();
        }
        ctrl.fileRows.forEach((fr) => {
          fr.setCollapsed(false);
        });
      };

      ctrl.toggleChanged = () => {
        ctrl.showChangedOnly = !ctrl.showChangedOnly;
        ctrl.flat = !!ctrl.showChangedOnly;
        updateFilePaths();
      };

      ctrl.select = (file) => {
        // ensures a given file is visible
        let fileRow = ctrl.fileRows.find(
          (fr) => fr.file === file || fr.path === file.path,
        );
        while (fileRow) {
          fileRow.setCollapsed(false);
          fileRow = fileRow.parent;
        }
      };

      ctrl.close = (file) => {
        if (file) {
          const fileRow = ctrl.fileRows.find((fr) => fr.file === file);
          ArrayUtils.remove(ctrl.fileRows, fileRow);
          ArrayUtils.remove(ctrl.files, file);
        }
      };

      ctrl.isOpen = (file) => {
        return ctrl.openFiles && ctrl.openFiles.includes(file);
      };

      ctrl.isChanged = (file) => {
        return ctrl.changedFiles && ctrl.changedFiles.includes(file);
      };

      ctrl._fileMenuClass = (fileRow) => {
        let fmClass = ctrl.fileMenuClass(fileRow);
        if (fmClass === false) {
          return '';
        } else {
          fmClass = fmClass || '';
        }
        if (fmClass) {
          fmClass += ' always-show';
        }
        if (ctrl.fileMenuTemplate) {
          fmClass += ' has-menu';
        }
        return fmClass;
      };
      let fileMenu = null;

      ctrl.fileMenuClick = ($event, fileRow, contextMenu) => {
        if (
          !ctrl.fileMenuTemplate ||
          ctrl.expired ||
          ctrl.readOnly ||
          ctrl.disabled
        )
          return;

        if (fileMenu) {
          const menuFileRow = fileMenu.fileRow;
          fileMenu.menu.close();
          if (fileRow === menuFileRow) {
            fileMenu = null;
            return;
          }
        }
        const scope = $scope.$parent.$new();
        scope.path = fileRow.path;
        scope.directory = fileRow.directory;
        scope.file = fileRow.file;
        scope.$index = fileRow.$index;
        fileMenu = {
          menu: bubbleDialogService.open(scope, $event.target, {
            className: 'file-tree-row-menu',
            menu: ctrl.fileMenuTemplate,
            position: contextMenu ? 'bottom' : 'right',
            mousePosition: contextMenu && $event,
          }),
          fileRow: fileRow,
        };
        fileMenu.menu.result.finally(() => {
          if (fileRow === fileMenu.fileRow) {
            fileMenu = null;
          }
        });
      };

      ctrl.startDragging = (file, event, external = true) => {
        const fileRow = ctrl.fileRows.find(
          (fr) => fr === file || fr.file === file,
        );
        ctrl.draggingFileRow = fileRow;
        if (fileRow && !external) {
          const contents =
            (fileRow.file && fileRow.file.contents) || fileRow.label;
          event.dataTransfer.setData('text/plain', contents);

          if (fileRow.filename && window.URL && window.File) {
            const data = URL.createObjectURL(
              new File([contents], fileRow.filename),
            );
            event.dataTransfer.setData(
              'DownloadUrl',
              `text/plain:${fileRow.filename}:${data}`,
            );
          }
          if (ctrl.onFileDragStart) {
            ctrl.onFileDragStart({
              file: fileRow.file,
              path: fileRow.path,
              event,
            });
          }
        }
      };

      ctrl.stopDragging = (external = true) => {
        ctrl.draggingFileRow = null;
        if (!external && ctrl.onFileDragStop) {
          ctrl.onFileDragStop();
        }
      };

      ctrl.dropFile = (fileRow, event) => {
        if (ctrl.onFileDrop) {
          ctrl.onFileDrop({
            file: ctrl.draggingFileRow.file,
            path: ctrl.draggingFileRow.path,
            dropPath: fileRow.path,
            dropDirectory: fileRow.directory,
            event,
          });
        }
      };

      const frWasCollapsed = {};

      function updateFilePaths() {
        const dirHash = {};
        ctrl.fileRows.forEach((fr) => {
          frWasCollapsed[fr.path] = fr.collapsed;
          if (fr.id) {
            frWasCollapsed[fr.id] = fr.collapsed;
          }
        });
        const fileRows = (ctrl.fileRows = []);
        let files = (ctrl.files || [])
          .filter(
            (f) =>
              (!ctrl.showChangedOnly || ctrl.isChanged(f)) &&
              (!f.hidden || ctrl.reviewMode), // remove hidden files at this level
          )
          .map((a) => {
            // we don't allow leading or trailing slashes, back slashes, or multiple slashes, so fix them
            if (a.path) {
              defineValue(a, '$path', cleanPath(a.path));
            }
            return a;
          });
        if (!ctrl.disableSort) {
          files.sort((a, b) => compareIgnoreCase(a.$path, b.$path));
        }
        files.forEach((file, index) => {
          const { pathParts, filename } = splitPath(file.$path);
          let parentPath;
          if (ctrl.flat) {
            // flattens the hierarchy
            parentPath = '';
          } else {
            // insert missing directories
            let checkedPath = [];
            pathParts.forEach((segment) => {
              const parentPath = checkedPath.join('/');
              checkedPath.push(segment);
              const virtualParentPath = checkedPath.join('/');
              if (!dirHash[virtualParentPath]) {
                const dir = (dirHash[virtualParentPath] = new FileRow({
                  tree: ctrl,
                  path: virtualParentPath,
                  filename: segment,
                  directory: true,
                  parent: dirHash[parentPath],
                  collapsed: frWasCollapsed[virtualParentPath],
                }));
                fileRows.push(dir);
              }
            });
            parentPath = pathParts.join('/');
          }

          // create internal model
          const fileRow = new FileRow(
            angular.extend({}, file, {
              tree: ctrl,
              file,
              filename,
              parent: dirHash[parentPath],
              collapsed:
                frWasCollapsed[file.id] !== undefined
                  ? frWasCollapsed[file.id]
                  : frWasCollapsed[file.$path],
            }),
          );

          // tracking original file
          fileRow.index = index;
          fileRows.push(fileRow);
          if (fileRow.directory) {
            dirHash[file.$path] = fileRow;
          }
        });

        sortFileRows();
        updateCompactedDirectories();

        if (ctrl.autoExpandAll) {
          ctrl.expandAll();
        }
      }

      function sortFileRows() {
        if (!ctrl.disableSort) {
          ctrl.fileRows.sort((a, b) => {
            const key = ctrl.flat
              ? 'label'
              : ctrl.prefs.directoriesOnTop
                ? 'dirTopSortPath'
                : 'dirInlineSortPath';
            return compareIgnoreCase(a[key], b[key]);
          });
        }
      }

      function updateCompactedDirectories() {
        if (ctrl.prefs.compactEmptyDirectories) {
          ctrl.fileRows
            .filter(
              (fr) =>
                fr.directory &&
                fr.children.length === 1 &&
                fr.children[0].directory,
            )
            .forEach((fr) => {
              fr.compacted = true;
            });
        } else {
          ctrl.fileRows.forEach((fr) => {
            fr.compacted = false;
          });
        }

        ctrl.fileRows.forEach((fr) => {
          fr.setDepth();
        });
      }

      //// preference syncing

      function loadPrefs() {
        const prefs = accountService.prefs();

        // Handle case where prefs.filetree might be a stringified JSON from old data
        if (typeof prefs.filetree === 'string') {
          try {
            prefs.filetree = JSON.parse(prefs.filetree);
          } catch (e) {
            $log.error('Error parsing filetree prefs:', e, prefs.filetree);
            // If parsing fails, reset to default
            prefs.filetree = null;
          }
        }

        ctrl.prefs = prefs.filetree = prefs.filetree || {
          directoriesOnTop: !KeyCodes.IS_MAC, // macs mix in directories by default
          compactEmptyDirectories: false,
        };
      }

      ctrl.setDirOnTop = (val) => {
        ctrl.prefs.directoriesOnTop = val;
        sortFileRows();
        if (!$rootScope.playbackToken) accountService.savePrefs();
      };
      ctrl.setCompactDir = (val) => {
        ctrl.prefs.compactEmptyDirectories = val;
        updateCompactedDirectories();
        if (!$rootScope.playbackToken) accountService.savePrefs();
      };

      $scope.$watch('$ctrl.prefs.directoriesOnTop', ctrl.setDirOnTop);
      $scope.$watch('$ctrl.prefs.compactEmptyDirectories', ctrl.setCompactDir);
    },
  )

  .directive('fileTreeDraggableFileRow', function ($timeout) {
    let counter = 0;
    return {
      restrict: 'A',
      require: '^fileTree',
      link(scope, elem, attr, fileTreeCtrl) {
        const getFileRow = () => scope.$eval(attr.fileTreeDraggableFileRow);
        let fileRow = getFileRow();

        const canDrag = () =>
          fileTreeCtrl.dragEnabled &&
          fileRow &&
          // either no file (virtual directory) or a non-locked, draggable file
          (!fileRow.file ||
            (!fileRow.file.disabled && fileRow.file.$draggable !== false));

        scope.$watch(canDrag, () => {
          elem.attr('draggable', canDrag());
        });

        let canDrop = false;
        let dragEnterCounter = 0;

        elem
          .attr('id', 'draggable-file-row-' + ++counter)

          // enable or disable drag-and-drop
          .attr('draggable', canDrag() || null)

          // enable dragging the file/filerow
          .on('dragstart', (event) => {
            scope.$apply(() => {
              fileTreeCtrl.startDragging(getFileRow(), event, false);
            });
          })
          .on('dragend', () => {
            $timeout(() => {
              fileTreeCtrl.stopDragging(false);
            });
          })

          // enable dropping a file/filerow here
          .on('dragenter', () => {
            dragEnterCounter++;
            const draggingFileRow = fileTreeCtrl.draggingFileRow;
            // save for future drag events
            canDrop =
              fileTreeCtrl.onFileDrop &&
              // we have a draggable row
              draggingFileRow &&
              !fileTreeCtrl.expired &&
              !fileTreeCtrl.disabled &&
              !fileTreeCtrl.readOnly &&
              // it's not the current row
              draggingFileRow !== getFileRow() &&
              // it either has to be a virtual directory, or a non-locked file
              (!draggingFileRow.file || !draggingFileRow.file.locked);
            if (canDrop) {
              elem.addClass('drag-over');
            }
          })
          .on('dragover', (event) => {
            if (canDrop) {
              stopEvent(event);
              event.dataTransfer.dropEffect = 'move';
            }
          })
          .on('dragleave', () => {
            dragEnterCounter--;
            if (dragEnterCounter < 1) {
              elem.removeClass('drag-over');
            }
          })
          .on('drop', (event) => {
            dragEnterCounter = 0;
            elem.removeClass('drag-over');
            if (canDrop) {
              stopEvent(event);
              scope.$applyAsync(() => {
                fileTreeCtrl.dropFile(getFileRow(), event);
              });
            }
          });
      },
    };
  });
